/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { Blocks } from 'lucide-react';
import { useContext } from 'react';

import { StepGroup } from './RunViewerSteps';

import { AIAtomicStep, BlockStep, Step } from '@/commons/data';
import { LiveAgentStepComponent } from '@/pages/projects/LiveAgentStepComponent';
import { Step as LiveStep } from '@/pages/projects/LiveAgentTypes';
import { StepContext } from '@/pages/projects/RunViewer';
import { cn } from '@/utils';

type StoryStepResultProps = {
    step: Step;
    defaultOpen: boolean;
    precomputedVariables?: { [key: string]: string };
    variables?: { [key: string]: string };
    debugMode?: boolean;
    hidePlaywright?: boolean;
};

const StoryStepResult = ({
    step,
    defaultOpen,
    debugMode = false,
    variables = {},
    precomputedVariables = {},
    hidePlaywright = false,
}: StoryStepResultProps) => {
    const { highlightedAction, highlightedStepRank, handleClickOnStepOrAction } = useContext(StepContext);

    const debugModeProps = debugMode ? { dispatch: () => {}, openStep: 'all', executeStep: () => {} } : {};
    if (
        step.kind === 'aiAgent' ||
        step.kind === 'recordedActions' ||
        (step.kind === 'aiAtomic' && (step as AIAtomicStep).action === 'selectOption')
    ) {
        return (
            <div
                className={cn(
                    'text-left cursor-pointer LiveAgentStep--executed overflow-hidden',
                    debugMode && 'flex gap-1',
                )}
                onClick={() => {
                    handleClickOnStepOrAction(step.rank);
                }}
            >
                {debugMode && <div className="font-mono">{step.rank}</div>}
                <LiveAgentStepComponent
                    step={{ ...step, executed: !debugMode, running: false, id: step.rank } as unknown as LiveStep}
                    {...debugModeProps}
                    inert
                    queued={step.rank === highlightedStepRank}
                    index={step.rank}
                    isDisabled={false}
                    variables={variables}
                    precomputedVariables={precomputedVariables}
                    editVariables={() => {}}
                    hidePlaywright={hidePlaywright}
                />
            </div>
        );
    }
    if (step.kind === 'block') {
        const parentStep = step as BlockStep;
        return (
            <>
                <StepGroup groupHeader={parentStep.name} icon={<Blocks />}>
                    {(parentStep.blockSteps || []).map((blockStep) => {
                        return (
                            <div
                                className={cn(
                                    'text-left cursor-pointer LiveAgentStep--executed',
                                    debugMode && 'flex gap-1',
                                )}
                                onClick={() => {
                                    handleClickOnStepOrAction(blockStep.rank);
                                }}
                                key={blockStep.rank}
                            >
                                {debugMode && <div className="font-mono">{blockStep.rank}</div>}
                                <LiveAgentStepComponent
                                    step={
                                        {
                                            ...blockStep,
                                            executed: !debugMode,
                                            running: false,
                                            id: blockStep.rank,
                                        } as unknown as LiveStep
                                    }
                                    {...debugModeProps}
                                    inert
                                    queued={blockStep.rank === highlightedStepRank}
                                    index={blockStep.rank}
                                    isDisabled={false}
                                    variables={variables}
                                    precomputedVariables={precomputedVariables}
                                    editVariables={() => {}}
                                    hidePlaywright={hidePlaywright}
                                />
                            </div>
                        );
                    })}
                </StepGroup>
            </>
        );
    }
    return (
        <div
            className={cn(
                'text-left cursor-pointer LiveAgentStep--executed StoryStepResult',
                debugMode && 'flex gap-1',
            )}
            onClick={() => {
                handleClickOnStepOrAction(step.rank);
            }}
        >
            {debugMode && <div className="font-mono">{step.rank}</div>}
            <LiveAgentStepComponent
                step={{ ...step, executed: !debugMode, running: false, id: step.rank } as unknown as LiveStep}
                {...debugModeProps}
                inert
                queued={step.rank === highlightedStepRank}
                index={step.rank}
                isDisabled={false}
                variables={variables}
                precomputedVariables={precomputedVariables}
                editVariables={() => {}}
                hidePlaywright={hidePlaywright}
            />
        </div>
    );
};

export default StoryStepResult;
